import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader } from "../ui";
import { unifiedContactService } from '../../services/unifiedContactService';
import ManualContactModal from '../ui/ManualContactModal';
import type { UnifiedContact } from '../../services/unifiedContactService';
// Icon used for people-related stats and contact rows
const PeopleIcon = ({ className = "" }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="9.5"
      cy="7"
      r="4"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M20 21v-2a4 4 0 0 0-3-3.87"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 3.13a4 4 0 0 1 0 7.75"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Icon used for email-related stats and contact metadata
const EmailIcon = ({ className = "" }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 6h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="m22 8-10 7L2 8"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Icon used for phone-related stats and contact metadata
const PhoneIcon = ({ className = "" }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.78.63 2.62a2 2 0 0 1-.45 2.11L8 9.91a16 16 0 0 0 6.09 6.09l1.46-1.29a2 2 0 0 1 2.11-.45c.84.3 1.72.51 2.62.63A2 2 0 0 1 22 16.92Z"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Frame142: React.FC = () => {
  const [allContacts, setAllContacts] = useState<UnifiedContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showManualModal, setShowManualModal] = useState(false);
  const [editingContact, setEditingContact] = useState<UnifiedContact | null>(null);
  const [manualSubmitting, setManualSubmitting] = useState(false);

  // Loads the current unified contact list for initial render and refreshes after edits
  const loadContacts = async () => {
    try {
      const contacts = await unifiedContactService.getAllContacts();
      setAllContacts(contacts);
    } catch (error) {
      console.error('[Frame142] Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Loads contacts once when the page mounts
  useEffect(() => {
    loadContacts();
  }, []);

  // Opens the modal in edit mode for the selected contact
  const handleEditContact = (contact: UnifiedContact) => {
    setEditingContact(contact);
    setShowManualModal(true);
  };

  // Resets modal state when the modal closes
  const handleModalClose = () => {
    setShowManualModal(false);
    setEditingContact(null);
  };

  // Creates a new contact or updates an existing one, then reloads the list
  const handleManualContactSubmit = async (contact: unknown) => {
    setManualSubmitting(true);
    try {
      if (editingContact) {
        await unifiedContactService.updateContact(editingContact.id, {
          firstName: contact.firstName.trim(),
          lastName: contact.lastName.trim(),
          email: contact.email?.trim() || '',
          phone: contact.phone?.trim(),
          relationship: contact.relationship?.toLowerCase().trim(),
          notes: contact.notes?.trim(),
        });
        console.log('[Frame142] Contact updated successfully:', contact);
        setEditingContact(null);
      } else {
        await unifiedContactService.createContact({
          firstName: contact.firstName.trim(),
          lastName: contact.lastName.trim(),
          email: contact.email?.trim() || '',
          phone: contact.phone?.trim(),
          relationship: contact.relationship?.toLowerCase().trim(),
          source: 'manual',
          notes: contact.notes?.trim(),
        });
        console.log('[Frame142] Contact created successfully:', contact);
      }

      const contacts = await unifiedContactService.getAllContacts();
      setAllContacts(contacts);
    } catch (error: unknown) {
      console.error('[Frame142] Failed to save contact:', error);
      if (error.code === 'DUPLICATE_CONTACT') {
        alert(`A contact with email ${contact.email} already exists.`);
      }
      throw error;
    } finally {
      setManualSubmitting(false);
    }
  };

  // Deletes a contact after confirmation, then reloads the list
  const handleDeleteContact = async (contactId: string) => {
    if (!confirm('Are you sure you want to delete this contact?')) return;
    try {
      await unifiedContactService.deleteContact(contactId);
      console.log('[Frame142] Contact deleted:', contactId);
      const contacts = await unifiedContactService.getAllContacts();
      setAllContacts(contacts);
    } catch (error) {
      console.error('[Frame142] Failed to delete contact:', error);
      alert('Failed to delete contact. Please try again.');
    }
  };

  // Summary cards shown above the contact list
  const statCards = [
    {
      label: 'Total People',
      value: allContacts.length,
      icon: <PeopleIcon className="w-6 h-6 text-cyan-700" />,
    },
    {
      label: 'With Email',
      value: allContacts.filter(c => c.email?.trim()).length,
      icon: <EmailIcon className="w-6 h-6 text-cyan-700" />,
    },
    {
      label: 'With Phone',
      value: allContacts.filter(c => c.phone?.trim()).length,
      icon: <PhoneIcon className="w-6 h-6 text-cyan-700" />,
    },
  ];

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="w-full max-w-[835px]">

          {/* Page header and primary action */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1">
              <ResponsiveHeader title="Your Trusted Circle" />
            </div>
            <div className="shrink-0">
              <button
                onClick={() => {
                  setEditingContact(null);
                  setShowManualModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-700 text-white text-sm font-medium rounded-md hover:bg-cyan-800 transition-colors whitespace-nowrap"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Person
              </button>
            </div>
          </div>

          {/* Intro copy for the trusted circle section */}
          <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
            List your key contacts such as family members, caregivers, executors, legal representatives so it's easy for others to know who's involved.
          </p>

          {/* Summary stats for the current contact list */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {statCards.map((card) => (
              <div
                key={card.label}
                className="bg-white rounded-md border border-stone-200 p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0">{card.icon}</div>

                  <div className="flex flex-col">
                    <div className="font-sans text-base text-stone-600">
                      {card.label}
                    </div>

                    <div className="font-sans text-3xl font-medium text-stone-800 leading-none mt-4">
                      {loading ? 'â€”' : card.value}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Full contact list with edit and delete actions */}
          <div className="mt-7">
            <div className="flex items-center gap-3 mb-3">
              <h2 className="font-sans text-xl font-medium text-stone-800">
                All Contacts
              </h2>
            </div>

            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-12 text-stone-500">
                  <div className="inline-block w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin mb-2" />
                  <p className="font-sans text-sm">Loading contacts...</p>
                </div>
              ) : allContacts.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-stone-300 rounded-lg">
                  <p className="font-sans text-stone-500 text-sm">No contacts yet. Add your first person above.</p>
                </div>
              ) : (
                allContacts.map((contact) => {
                  const relationshipText = contact.relationship
                    ? contact.relationship.charAt(0).toUpperCase() + contact.relationship.slice(1)
                    : null;

                  return (
                    <div
                      key={contact.id}
                      className="flex flex-col sm:flex-row sm:items-start gap-4 bg-white rounded-lg border border-stone-200 p-6"
                    >
                      {/* Contact icon and main details */}
                      <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 flex-1 min-w-0">
                        <PeopleIcon className="w-6 h-6 text-cyan-700 flex-shrink-0 mt-0.5" />

                        <div className="flex-1 min-w-0">
                          <div className="font-sans text-lg font-medium text-stone-800">
                            {contact.firstName} {contact.lastName}
                          </div>

                          {relationshipText && (
                            <div className="font-sans text-base text-stone-600 mt-1">
                              {relationshipText}
                            </div>
                          )}

                          {(contact.email || contact.phone) && (
                            <div className="flex flex-wrap gap-x-5 gap-y-2 font-sans text-base text-stone-600 mt-2">
                              {contact.email && (
                                <div className="flex items-center gap-2 min-w-0">
                                  <EmailIcon className="w-4 h-4 text-cyan-700 flex-shrink-0" />
                                  <span className="break-all">{contact.email}</span>
                                </div>
                              )}

                              {contact.phone && (
                                <div className="flex items-center gap-2 min-w-0">
                                  <PhoneIcon className="w-4 h-4 text-cyan-700 flex-shrink-0" />
                                  <span>{contact.phone}</span>
                                </div>
                              )}
                            </div>
                          )}

                          {contact.notes && (
                            <div className="font-sans text-sm text-stone-500 mt-2">
                              {contact.notes}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Row actions for editing or deleting a contact */}
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => handleEditContact(contact)}
                          className="text-cyan-600 hover:text-cyan-800 px-3 py-1 rounded border border-cyan-400 hover:border-cyan-700 font-sans text-sm font-medium whitespace-nowrap transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteContact(contact.id)}
                          className="text-red-600 hover:text-red-800 px-3 py-1 rounded border border-red-300 hover:border-red-700 font-sans text-sm font-medium whitespace-nowrap transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </ResponsiveContent>

      {/* Modal used for both creating a new contact and editing an existing one */}
      <ManualContactModal
        open={showManualModal}
        onClose={handleModalClose}
        onSubmit={handleManualContactSubmit}
        loading={manualSubmitting}
        initialData={
          editingContact
            ? {
                firstName: editingContact.firstName,
                lastName: editingContact.lastName,
                email: editingContact.email || '',
                phone: editingContact.phone || '',
                relationship: editingContact.relationship || '',
                notes: editingContact.notes || '',
              }
            : undefined
        }
      />
    </ResponsiveContainer>
  );
};

export default Frame142;